import { CloudIcon, ExternalLinkIcon, MonitorIcon } from "lucide-react";
import type { CmmdBoardTask, CmmdWorkSession } from "@cmmd-forge/contracts";

import { cn } from "~/lib/utils";
import { Skeleton } from "~/components/ui/skeleton";
import type { ForgeThreadTarget } from "./forgeExecutionTarget";

export interface ProjectActiveWorkSummaryProps {
  readonly sessions: readonly CmmdWorkSession[];
  readonly tasks: readonly CmmdBoardTask[];
  readonly loading: boolean;
  readonly error: string | null;
  readonly onOpenThread: (target: ForgeThreadTarget) => void;
}

export function ProjectActiveWorkSummary(props: ProjectActiveWorkSummaryProps) {
  if (props.loading) {
    return (
      <div aria-label="Loading active work" className="space-y-2">
        <Skeleton className="h-12 rounded-lg border border-border" />
        <Skeleton className="h-12 rounded-lg border border-border" />
      </div>
    );
  }
  if (props.error) {
    return <p className="text-xs text-destructive">{props.error}</p>;
  }
  if (props.sessions.length === 0) {
    // Empty state is omitted by the Overview rail; keep a quiet fallback for other hosts.
    return null;
  }
  return (
    <ol className="max-h-56 space-y-1 overflow-y-auto overscroll-contain pr-1">
      {props.sessions.map((session) => (
        <ActiveWorkRow
          key={session.id}
          session={session}
          taskTitle={findTaskTitle(props.tasks, session.taskId)}
          onOpenThread={props.onOpenThread}
        />
      ))}
    </ol>
  );
}

function ActiveWorkRow(props: {
  readonly session: CmmdWorkSession;
  readonly taskTitle: string;
  readonly onOpenThread: (target: ForgeThreadTarget) => void;
}) {
  const rootThread = props.session.threads.find((thread) => thread.kind === "root") ?? null;
  const RuntimeIcon = props.session.runtime.source === "forge-cloud" ? CloudIcon : MonitorIcon;
  const content = (
    <>
      <span className="flex size-7 shrink-0 items-center justify-center rounded-md bg-muted text-muted-foreground">
        <RuntimeIcon className="size-3.5" aria-hidden />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block truncate text-xs font-medium text-foreground">
          {props.taskTitle}
        </span>
        <span className="mt-0.5 flex min-w-0 items-center gap-1.5 text-[11px] text-muted-foreground">
          <span className="truncate">{props.session.runtime.displayName}</span>
          <span aria-hidden>·</span>
          <span
            className={cn(
              "shrink-0 capitalize",
              props.session.status === "active"
                ? "text-success-foreground"
                : "text-muted-foreground",
            )}
          >
            {props.session.status}
          </span>
        </span>
      </span>
      {rootThread ? (
        <ExternalLinkIcon className="size-3.5 shrink-0 text-muted-foreground" aria-hidden />
      ) : null}
    </>
  );

  return (
    <li>
      {rootThread ? (
        <button
          type="button"
          onClick={() =>
            props.onOpenThread({
              environmentId: props.session.forgeProject.environmentId,
              threadId: rootThread.forgeThreadId,
            })
          }
          className="flex min-h-12 w-full items-center gap-2 rounded-xl bg-muted/30 px-2.5 py-2 text-left transition-colors duration-150 hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          {content}
        </button>
      ) : (
        <div className="flex min-h-12 items-center gap-2 rounded-xl bg-muted/30 px-2.5 py-2">
          {content}
        </div>
      )}
    </li>
  );
}

function findTaskTitle(tasks: readonly CmmdBoardTask[], taskId: number): string {
  for (const task of tasks) {
    if (task.id === taskId) return task.title;
    const childTitle = findTaskTitleOrNull(task.children, taskId);
    if (childTitle) return childTitle;
  }
  return `Task #${taskId}`;
}

function findTaskTitleOrNull(tasks: readonly CmmdBoardTask[], taskId: number): string | null {
  for (const task of tasks) {
    if (task.id === taskId) return task.title;
    const childTitle = findTaskTitleOrNull(task.children, taskId);
    if (childTitle) return childTitle;
  }
  return null;
}
